import React, { useState, useRef  } from "react";
import { useRole } from "../../hooks/useRole";

export interface Patient {
  id: string;
  display_id?: string;
  name: string;
  avatar?: string;
  avatarColor?: string;
  consultationType: string;
  patientSince: string;
  age: number;
  gender: string;
  bloodGroup: string;
  contact: string;
  lastVisit: string;
  status: "Active" | "Inactive";
  tags?: string[];
}

interface PatientRowProps {
  patient: Patient;
  onDelete?: (patientId: string) => void;
  onRestore?: (patientId: string) => void;
  onPermanentDelete?: (patientId: string) => void;
  onView?: (patient: Patient) => void;
  variant?: "mobile" | "desktop";
}

export const PatientRow: React.FC<PatientRowProps> = ({
  patient,
  onDelete,
  onRestore,
  onPermanentDelete,
  onView,
  variant = "desktop",
}) => {
  const [showMenu, setShowMenu] = useState(false);
  // Gated on the grant, not the role label. Deleting used to be "Admin-only", so
  // isAdmin is the legacy fallback for a backend that doesn't send grants — but
  // once it does, unticking Delete Patients removes this item for an Admin too.
  // DELETE /patients/{id} enforces records.delete regardless.
  const { canOr, isAdmin } = useRole();
  const canDelete = canOr("records.delete", isAdmin);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getBloodGroupColor = (bloodGroup: string) => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      "A+": { bg: "#ECFDF5", text: "#059669", border: "#A7F3D0" },
      "A-": { bg: "#EFF6FF", text: "#2563EB", border: "#BFDBFE" },
      "B+": { bg: "#FEF3C7", text: "#D97706", border: "#FDE68A" },
      "B-": { bg: "#FEE2E2", text: "#DC2626", border: "#FECACA" },
      "AB+": { bg: "#F3E8FF", text: "#7C3AED", border: "#DDD6FE" },
      "AB-": { bg: "#FCE7F3", text: "#DB2777", border: "#FBCFE8" },
      "O+": { bg: "#ECFEFF", text: "#0891B2", border: "#A5F3FC" },
      "O-": { bg: "#F1F5F9", text: "#475569", border: "#CBD5E1" },
    };
    return colors[bloodGroup] || colors["O+"];
  };

  const bloodColors = getBloodGroupColor(patient.bloodGroup);

  const ActionMenu = () => (
    // stopPropagation so opening the menu / clicking Delete doesn't also
    // trigger the row's navigate-to-profile click.
    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        ref={menuBtnRef}
        type="button"
        onClick={() => {
          if (!showMenu && menuBtnRef.current) {
            const rect = menuBtnRef.current.getBoundingClientRect();
            setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
          }
          setShowMenu(!showMenu);
        }}
        className="flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2"/>
          <circle cx="12" cy="12" r="2"/>
          <circle cx="12" cy="19" r="2"/>
        </svg>
      </button>

      {showMenu && menuPos && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowMenu(false)}
          />
          <div
            className="fixed z-50 w-32 rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
            style={{ top: menuPos.top, right: menuPos.right }}
          >
            <button
              type="button"
              onClick={() => { onView?.(patient); setShowMenu(false); }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              View
            </button>
            {canDelete && patient.status === "Active" && (
              <button
                type="button"
                onClick={() => { onDelete?.(patient.id); setShowMenu(false); }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
                Delete
              </button>
            )}
            {canDelete && patient.status === "Inactive" && (
              <>
                <button
                  type="button"
                  onClick={() => { onRestore?.(patient.id); setShowMenu(false); }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-emerald-600 hover:bg-emerald-50"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12a9 9 0 1 0 3-6.7L3 8"/>
                    <path d="M3 3v5h5"/>
                  </svg>
                  Restore
                </button>
                <button
                  type="button"
                  onClick={() => { onPermanentDelete?.(patient.id); setShowMenu(false); }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                  Delete Permanently
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );

  // Mobile Card View
  if (variant === "mobile") {
    return (
      <div
        className="border-b border-slate-100 p-4 hover:bg-slate-50/50 cursor-pointer"
        onClick={() => onView?.(patient)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
              style={{ backgroundColor: patient.avatarColor || "#8B5CF6" }}
            >
              {patient.avatar ? (
                <img src={patient.avatar} alt={patient.name} className="h-full w-full rounded-full object-cover" />
              ) : (
                getInitials(patient.name)
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => onView?.(patient)}
                  className="text-sm font-medium text-slate-800 hover:text-indigo-600 hover:underline text-left"
                >
                  {patient.name}
                </button>
                <span
                  className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium"
                  style={{
                    backgroundColor: patient.status === "Active" ? "#ECFDF5" : "#FEF2F2",
                    color: patient.status === "Active" ? "#059669" : "#DC2626",
                  }}
                >
                  {patient.status}
                </span>
              </div>
              <p className="text-xs text-slate-400">{patient.consultationType}</p>
              <p className="mt-0.5 text-[10px] text-slate-400">
                Patient Since: <span className="text-slate-600">{patient.patientSince}</span>
              </p>
            </div>
          </div>
          <ActionMenu />
        </div>

        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-slate-400">Age:</span>
            <span className="text-slate-600">{patient.age} years</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400">Gender:</span>
            <span className="text-slate-600">{patient.gender}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400">Blood:</span>
            <span
              className="inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium"
              style={{
                backgroundColor: bloodColors.bg,
                color: bloodColors.text,
                border: `1px solid ${bloodColors.border}`,
              }}
            >
              {patient.bloodGroup}
            </span>
          </div>
          <div className="flex items-center gap-1.5 min-w-0">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
            <span className="text-slate-600 truncate">{patient.contact}</span>
          </div>
        </div>
      </div>
    );
  }

  // Desktop Table Row
  return (
    <tr
      className="border-b border-slate-100 hover:bg-slate-50/50 cursor-pointer"
      onClick={() => onView?.(patient)}
    >
      {/* Patient Name */}
      <td className="px-4 py-3" style={{ width: '22%' }}>
        <div className="flex items-start gap-3">
          <div
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
            style={{ backgroundColor: patient.avatarColor || "#8B5CF6" }}
          >
            {patient.avatar ? (
              <img src={patient.avatar} alt={patient.name} className="h-full w-full rounded-full object-cover" />
            ) : (
              getInitials(patient.name)
            )}
          </div>
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => onView?.(patient)}
              className="text-sm font-medium text-slate-800 hover:text-indigo-600 hover:underline text-left"
            >
              {patient.name}
            </button>
            <p className="text-xs text-slate-400">{patient.consultationType}</p>
          </div>
        </div>
        <p className="mt-1 text-xs text-slate-400">
          Patient Since: <span className="text-slate-600">{patient.patientSince}</span>
        </p>
      </td>

      {/* Age */}
      <td className="px-4 py-3 align-middle" style={{ width: '8%' }}>
        <div className="text-xs text-slate-600">
          <span>{patient.age}</span>
          <br />
          <span className="text-slate-400">years</span>
        </div>
      </td>

      {/* Gender */}
      <td className="px-4 py-3 align-middle" style={{ width: '10%' }}>
        <span className="text-xs text-slate-600">{patient.gender}</span>
      </td>

      {/* Blood Group */}
      <td className="px-4 py-3 align-middle" style={{ width: '8%' }}>
        <span
          className="inline-flex rounded px-2 py-0.5 text-xs font-medium"
          style={{
            backgroundColor: bloodColors.bg,
            color: bloodColors.text,
            border: `1px solid ${bloodColors.border}`,
          }}
        >
          {patient.bloodGroup}
        </span>
      </td>

      {/* Contact */}
      <td className="px-4 py-3 align-middle" style={{ width: '16%' }}>
        <div className="flex items-center gap-1.5 text-xs text-slate-600">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
          </svg>
          <span>{patient.contact}</span>
        </div>
      </td>

      {/* Last Visit */}
      <td className="px-4 py-3 align-middle" style={{ width: '12%' }}>
        <div className="flex items-center gap-1.5 text-xs text-slate-600">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/>
            <line x1="16" x2="16" y1="2" y2="6"/>
            <line x1="8" x2="8" y1="2" y2="6"/>
            <line x1="3" x2="21" y1="10" y2="10"/>
          </svg>
          <span>{patient.lastVisit}</span>
        </div>
      </td>

      {/* Status */}
      <td className="px-4 py-3 align-middle" style={{ width: '10%' }}>
        <span
          className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium"
          style={{
            backgroundColor: patient.status === "Active" ? "#ECFDF5" : "#FEF2F2",
            color: patient.status === "Active" ? "#059669" : "#DC2626",
          }}
        >
          {patient.status}
        </span>
      </td>

      {/* Actions */}
      <td className="px-4 py-3 align-middle text-center" style={{ width: '14%' }}>
        <ActionMenu />
      </td>
    </tr>
  );
};

export default PatientRow;
